import {useMemo, useState} from 'react'

import {businessCalendarDate} from 'src/api/business-time'
import {useDispatchBoard, useDispatchYards} from 'src/dispatch/use-dispatch'
import type {DispatchTimelineData} from 'src/dispatch-timeline/types'
import {useGoogleMapsProviderStatus} from 'src/jobs/google-maps/google-maps-provider'
import {toApiDateString} from 'src/jobs/job-schedule-format'

import {useGoogleLegTimes} from './run-map-drive-times'
import {
    buildDriverRun,
    buildRunMapJobs,
    calendarDaysBetween,
    defaultMapDay,
    driversOnDay,
    evaluateRun,
    jobsOnYardBoard,
    runMapJobExtras,
    startYardForDriver,
} from './run-map-logic'
import {type RunMapMode, RunMapPanel} from './run-map-panel'

export const NO_KEY_NOTE = 'Google Maps key not set'
export const GOOGLE_FAILED_NOTE = 'Google Maps could not load. Showing schematic view.'

type RunMapViewProps = {
    /** Timeline data for all yards. Filtered by `yardId` here. */
    data: DispatchTimelineData
    yardId: string | null
    customerId?: string | null
    onOpenJob: (jobId: string) => void
}

export function RunMapView({data, yardId, customerId = null, onOpenJob}: RunMapViewProps) {
    const today = toApiDateString(businessCalendarDate())
    const days = useMemo(
        () =>
            data.scheduleWindowStart && data.scheduleWindowEnd
                ? calendarDaysBetween(data.scheduleWindowStart, data.scheduleWindowEnd)
                : [today],
        [data.scheduleWindowEnd, data.scheduleWindowStart, today],
    )
    const [pickedDay, setPickedDay] = useState<string | null>(null)
    const day =
        pickedDay != null && days.includes(pickedDay)
            ? pickedDay
            : (defaultMapDay(days, today) ?? today)
    const [pickedDriverId, setPickedDriverId] = useState<string | null>(null)

    const {data: yards = []} = useDispatchYards()
    // Load job locations and closer-yard fields for the selected day.
    const boardDayQuery = useDispatchBoard(day, day)
    const extras = useMemo(
        () => (boardDayQuery.data ? runMapJobExtras(boardDayQuery.data) : {}),
        [boardDayQuery.data],
    )

    const allJobs = useMemo(() => buildRunMapJobs(data, day, extras), [data, day, extras])
    const jobs = useMemo(
        () => jobsOnYardBoard(allJobs, yardId, customerId),
        [allJobs, customerId, yardId],
    )
    const drivers = useMemo(() => driversOnDay(jobs), [jobs])
    const driverId = drivers.some((driver) => driver.id === pickedDriverId) ? pickedDriverId : null

    const baseRuns = useMemo(() => {
        const homeYardIds = Object.fromEntries(
            data.resources.map((resource) => [resource.id, resource.homeYardId]),
        )
        return drivers.map((driver) =>
            buildDriverRun(
                allJobs,
                driver,
                startYardForDriver(driver.id, allJobs, yards, yardId, homeYardIds),
            ),
        )
    }, [allJobs, data.resources, drivers, yardId, yards])

    const googleStatus = useGoogleMapsProviderStatus()
    const allLegs = useMemo(() => baseRuns.flatMap((run) => run.legs), [baseRuns])
    const googleTimes = useGoogleLegTimes(allLegs, googleStatus === 'ready')
    const runs = baseRuns.map((run) => evaluateRun(run, googleTimes))

    const mode: RunMapMode =
        googleStatus === 'ready' ? 'google' : googleStatus === 'loading' ? 'loading' : 'schematic'
    const schematicNote =
        googleStatus === 'missing'
            ? NO_KEY_NOTE
            : googleStatus === 'ready' || googleStatus === 'loading'
              ? null
              : GOOGLE_FAILED_NOTE

    return (
        <RunMapPanel
            days={days}
            day={day}
            today={today}
            onDayChange={setPickedDay}
            drivers={drivers}
            driverId={driverId}
            onDriverChange={setPickedDriverId}
            jobs={jobs}
            yards={yards}
            shownYardIds={yardId ? [yardId] : yards.map((yard) => yard.id)}
            runs={runs}
            mode={mode}
            schematicNote={schematicNote}
            locationsLoading={boardDayQuery.isLoading}
            locationsError={boardDayQuery.error ? boardDayQuery.error.message : null}
            onOpenJob={onOpenJob}
        />
    )
}
